import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useNotifications, SimpleModal } from '@sequencehq/core-components'
import {
  invoiceCustomerBillingAddressAtom,
  invoiceCustomerLegalCompanyNameAtom,
  invoiceIdAtom,
  isSavePendingAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { useMissingCustomerAddressToast } from 'common/hooks'
import { useInvalidateInvoice } from 'Invoices/hooks/useInvalidateInvoice'
import { useAtomValue } from 'jotai'
import { ReactElement } from 'react'
import { ButtonReviewAndSend } from '../ButtonReviewAndSend'
import { SendInvoiceModalBody } from 'Invoices/components/CreateEditShared'
import { useHumanReadableTotalAfterCreditGrants } from 'Invoices/hooks/useCreditGrantTotals'

export const ButtonSendInvoice = () => {
  const isSavePending = useAtomValue(isSavePendingAtom)

  return (
    <DialogSendInvoice
      trigger={
        <ButtonReviewAndSend
          disabled={isSavePending}
          data-testid="header.button.send-invoice"
        />
      }
    />
  )
}

const DialogSendInvoice = ({ trigger }: { trigger: ReactElement }) => {
  const { displayNotification } = useNotifications()
  const invalidateInvoice = useInvalidateInvoice()

  const invoiceId = useAtomValue(invoiceIdAtom)
  const totalAfterCredit = useHumanReadableTotalAfterCreditGrants()
  const customerLegalName = useAtomValue(invoiceCustomerLegalCompanyNameAtom)
  const customerBillingAddress = useAtomValue(invoiceCustomerBillingAddressAtom)
  const showMissingAddressToast = useMissingCustomerAddressToast()

  const sendInvoiceMutation = useMutation(
    dashboard20240730Client.postSendInvoice,
    {
      onSuccess: () => {
        invalidateInvoice()
        displayNotification('Invoice sent', { type: 'success' })
      },
      onError: () => {
        displayNotification('Failed to send invoice', {
          type: 'error'
        })
      }
    }
  )

  return (
    <SimpleModal
      trigger={trigger}
      variant="dialog"
      title={`Send invoice for ${totalAfterCredit} to ${customerLegalName}?`}
      submitButtonText="Send invoice"
      submittingButtonText="Sending..."
      onSubmit={async () => {
        if (!invoiceId) return false

        // The backend rejects the send without a billing address, with an
        // error that does not say why. Stop here and name the real problem.
        if (!customerBillingAddress) {
          showMissingAddressToast('sending this invoice')
          return false
        }
        const res = await sendInvoiceMutation.mutateAsync({
          id: invoiceId
        })
        return Boolean(res)
      }}
    >
      <SendInvoiceModalBody />
    </SimpleModal>
  )
}
